import {
  IWebflowBenefitCategory,
  IWebflowBenefitMinimumSubscription
} from "@wingspanhq/cms/dist/lib/interfaces";
import {
  SubscriptionPackage,
  SubscriptionPackageTier
} from "@wingspanhq/users/dist/lib/interfaces";
import bcmgLogo from "../../assets/axis/bcmg-logo.png";
import manhattanLifeLogo from "../../assets/axis/manhattan-life-logo.png";
import mutualOfOmahaLogo from "../../assets/axis/mutual-of-omaha-logo.png";
import mutualTrustLogo from "../../assets/axis/mutual-trust-logo.png";
import transamericaLogo from "../../assets/axis/transamerica-logo.png";
import unitedHealthcareLogo from "../../assets/axis/united-healthcare-logo.png";
import { ExtendedWebflowBenefit } from "../../types";

export enum AxisBenefitSlug {
  TransamericaFinalExpense = "transamerica-final-expense",
  MutualTrustWholeLifeIUL = "mutual-trust-whole-life-iul",
  MutualOfOmahaTermLife = "mutual-of-omaha-term-life",
  ManhattanLifeHospitalIndemnity = "manhattan-life-hospital-indemnity",
  UnitedHealthcare = "united-healthcare",
  BCMGRetirementInvestment = "bcmg-retirement-investment"
}

export const axisBenefits: ExtendedWebflowBenefit[] = [
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Final Expense Coverage with Funeral Concierge",
    itemId: AxisBenefitSlug.TransamericaFinalExpense,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.TransamericaFinalExpense,

    // Category and subscription
    category: IWebflowBenefitCategory.Insurance,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "shield-heart-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: transamericaLogo,
      placeholder: ""
    },
    helperLogo: {
      src: transamericaLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance:
      "Lifetime protection for final expenses plus 24/7 concierge funeral planning.",
    mainHeader: "Final Expense Coverage with Funeral Concierge",
    mainDescription:
      "Transamerica's Final Expense plan ensures your loved ones aren't burdened with funeral or end-of-life costs. Coverage is simple to apply for, and includes a built-in concierge service to guide your family through funeral planning and related arrangements.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "What is Final Expense Insurance?\nA permanent life policy covering funeral and burial costs.\n-\nIs there a medical exam?\nOften simplified or no-exam underwriting.\n-\nWhat is the concierge service?\nA 24/7 support resource to help with funeral coordination and paperwork.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 12,
    hideCost: true
  },
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Whole Life / Indexed Universal Life (IUL)",
    itemId: AxisBenefitSlug.MutualTrustWholeLifeIUL,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.MutualTrustWholeLifeIUL,

    // Category and subscription
    category: IWebflowBenefitCategory.Insurance,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "piggy-bank-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: mutualTrustLogo,
      placeholder: ""
    },
    helperLogo: {
      src: mutualTrustLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance:
      "Build cash value while securing lifelong protection and financial flexibility.",
    mainHeader: "Whole Life / Indexed Universal Life (IUL)",
    mainDescription:
      "Mutual Trust, a Pan-American Life company, offers permanent life insurance options designed to build long-term cash value. Whole life and IUL policies provide steady growth potential, access to policy loans, and lifetime protection—ideal for financial independence and future planning.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "What is Whole Life Insurance?\nPermanent protection that builds guaranteed cash value.\n-\nWhat is an IUL?\nA policy that links cash value growth to a market index while protecting from market loss.\n-\nCan I borrow from my policy?\nYes, policy loans are available once value accumulates.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 13,
    hideCost: true
  },
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Term Life (10, 15, 20, 30-Year)",
    itemId: AxisBenefitSlug.MutualOfOmahaTermLife,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.MutualOfOmahaTermLife,

    // Category and subscription
    category: IWebflowBenefitCategory.Insurance,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "calendar-check-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: mutualOfOmahaLogo,
      placeholder: ""
    },
    helperLogo: {
      src: mutualOfOmahaLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance: "Affordable protection for the years you need it most.",
    mainHeader: "Term Life (10, 15, 20, 30-Year)",
    mainDescription:
      "Mutual of Omaha offers level-term life insurance designed to protect your income and family during key earning years. Choose 10-, 15-, 20-, or 30-year terms with level premiums and the option to convert to permanent coverage later on.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "Why term life?\nCost-effective coverage during high-need years.\n-\nCan I extend or convert later?\nYes, conversion options are available.\n-\nIs the death benefit guaranteed?\nYes, as long as premiums are paid on time.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 14,
    hideCost: true
  },
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Hospital Indemnity",
    itemId: AxisBenefitSlug.ManhattanLifeHospitalIndemnity,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.ManhattanLifeHospitalIndemnity,

    // Category and subscription
    category: IWebflowBenefitCategory.Insurance,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "hospital-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: manhattanLifeLogo,
      placeholder: ""
    },
    helperLogo: {
      src: manhattanLifeLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance:
      "Cash benefits for unexpected hospital stays and recovery expenses.",
    mainHeader: "Hospital Indemnity",
    mainDescription:
      "Manhattan Life's Hospital Indemnity plan pays cash directly to you to help with costs during hospital stays or outpatient recovery. Use the benefit however you choose—for bills, travel, or daily living expenses—and stay financially protected during health challenges.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "Is this health insurance?\nNo, it supplements your health coverage.\n-\nHow can the benefit be used?\nFunds can be used for any personal expense.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 15,
    hideCost: true
  },
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Health Insurance – PPO, HMO, and Group Health Options",
    itemId: AxisBenefitSlug.UnitedHealthcare,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.UnitedHealthcare,

    // Category and subscription
    category: IWebflowBenefitCategory.Health,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "heart-pulse-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: unitedHealthcareLogo,
      placeholder: ""
    },
    helperLogo: {
      src: unitedHealthcareLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance:
      "Comprehensive medical coverage with flexible network choices for individuals and groups.",
    mainHeader: "Health Insurance – PPO, HMO, and Group Health Options",
    mainDescription:
      "UnitedHealthcare offers a range of health plans designed to fit diverse needs and budgets—whether individual PPO and HMO coverage or group health options for organizations. Plans include access to one of the nation's largest provider networks, preventive care, prescription benefits, and virtual health services.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "What's the difference between PPO and HMO?\nPPO plans offer more provider flexibility, while HMOs are typically lower-cost with coordinated care.\n-\nAre preventive services covered?\nYes, annual checkups and screenings are generally included at no additional cost.\n-\nCan this be offered to groups?\nYes, group health options are available for organizations and their independent contractors.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 16,
    hideCost: true
  },
  {
    // Required IWebflowItem fields
    collectionId: "axis",
    editedOn: new Date(),
    isDraft: false,
    name: "Retirement, Investment, and Wealth Management",
    itemId: AxisBenefitSlug.BCMGRetirementInvestment,
    publishedOn: new Date(),
    slug: AxisBenefitSlug.BCMGRetirementInvestment,

    // Category and subscription
    category: IWebflowBenefitCategory.Finances,
    minSubscription: IWebflowBenefitMinimumSubscription.Benefits,
    minSubscriptionPackage: SubscriptionPackage.Benefits,
    minSubscriptionPackageTier: SubscriptionPackageTier.Basic,

    // Visual elements
    icon: "graph-up-arrow",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    logo: {
      src: bcmgLogo,
      placeholder: ""
    },
    helperLogo: {
      src: bcmgLogo,
      placeholder: ""
    },
    mainVideo: {
      src: ""
    },

    // Content
    atAGlance:
      "Simplified investing and retirement planning through BCMG, a registered investment advisor.",
    mainHeader: "Retirement, Investment, and Wealth Management",
    mainDescription:
      "Through BCMG, a registered investment advisor, contractors and companies gain access to modern retirement and investment tools. Advisors help clients plan, invest, and build long-term financial security through an integrated digital platform.",

    // FAQs
    mainFAQSHeader: "FAQs",
    mainFAQSItems:
      "Who manages the investments?\nLicensed advisors under Blasen Capital Markets Group.\n-\nWhat accounts are available?\nIRAs, 401ks, brokerage, and managed portfolios.\n-\nIs there a minimum investment?\nAccount minimums vary.",

    // Contact information
    helperContactInfoDescription: "Contact",
    helperContactInfoEmail: "Support@axis1099.com",
    helperContactInfoPhone: "517-899-0154",

    // Display settings
    featured: false,
    hidden: false,
    isAdditionalCoverage: true,
    helperHideMemberAndGroupInfo: true,
    orderPriority: 17,
    hideCost: true
  }
];
